import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { Box, InputAdornment, InputLabel, Stack, TextField } from '@mui/material';

// Câmp de culoare (TextField cu swatch + <input type="color"> în adornment).
// Pickerul nativ emite `change` la fiecare tick de drag — swatch-ul urmărește instant
// (state local), dar propagarea către formular e debounce-uită (model GeoPuls), cu
// flush imediat la închiderea pickerului ca să nu se piardă valoarea finală.
const COLOR_DEBOUNCE_MS = 150;

export const ColorField: FC<{
	label: string;
	value?: string;
	onChange: (v: string) => void;
}> = ({ label, value, onChange }) => {
	const [pickerValue, setPickerValue] = useState(value || '#000000');
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// resincronizează swatch-ul când valoarea se schimbă din exterior (ex. schimbare tip)
	useEffect(() => {
		setPickerValue(value || '#000000');
	}, [value]);

	useEffect(
		() => () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		},
		[]
	);

	const handlePickerChange = (e: ChangeEvent<HTMLInputElement>): void => {
		const next = e.target.value;
		setPickerValue(next);
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => onChange(next), COLOR_DEBOUNCE_MS);
	};

	const handlePickerBlur = (e: ChangeEvent<HTMLInputElement>): void => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
		onChange(e.target.value);
	};

	return (
		<Stack sx={{ gap: 0.75 }}>
			<InputLabel>{label}</InputLabel>
			<TextField
				value={value ?? ''}
				onChange={(e) => onChange(e.target.value)}
				placeholder="#000000"
				sx={{ width: 220 }}
				slotProps={{
					input: {
						startAdornment: (
							<InputAdornment position="start">
								<Box
									sx={{
										position: 'relative',
										width: 22,
										height: 22,
										borderRadius: 0.75,
										border: '1px solid',
										borderColor: 'divider',
										bgcolor: pickerValue,
										overflow: 'hidden'
									}}
								>
									<Box
										component="input"
										type="color"
										value={pickerValue}
										onChange={handlePickerChange}
										onBlur={handlePickerBlur}
										aria-label={label}
										sx={{
											position: 'absolute',
											inset: 0,
											width: '100%',
											height: '100%',
											opacity: 0,
											border: 0,
											p: 0,
											cursor: 'pointer'
										}}
									/>
								</Box>
							</InputAdornment>
						)
					}
				}}
			/>
		</Stack>
	);
};

ColorField.defaultProps = { value: undefined };
